/**
 * close.ts — close + booking support content (GUI v2 feature 7).
 *
 * Sources of truth: 14_deposit-close-script-library.md (the $100 deposit
 * motion, 12-hour window, disclosure block), 15_showroom-consult-script-library.md
 * (showroom/consult/inspection invites, both locations), 16_crm-capture-script-
 * library.md (capture scripts + the end-of-call completeness sweep, which runs
 * as the wrap-up completeness check). Items with status "pending" sit on TBDs.
 */

export interface CloseStep {
  id: string;
  /** ≤40-char checklist line. */
  label: string;
  /** Verbatim-sayable script line. */
  line: string;
  required: boolean;
  source: string;
}

/** The deposit motion, in order (14 §§1–5). */
export const DEPOSIT_MOTION: CloseStep[] = [
  {
    id: "two_times",
    label: "Offer two specific times",
    line: "Let's get you on the schedule. I've got Tuesday morning or Thursday morning — which works better?",
    required: true,
    source: "14 §1",
  },
  {
    id: "deposit_ask",
    label: "$100 deposit + text-to-pay link",
    line: "There's a $100 deposit that goes toward your total. I'll text you a secure payment link right now.",
    required: true,
    source: "14 §1",
  },
  {
    id: "verify_details",
    label: "Stay on line — verify vehicle",
    line: "No rush, I'll stay on while it comes through. Let me make sure I've got your vehicle right.",
    required: true,
    source: "14 §1",
  },
  {
    id: "confirm_paid",
    label: "Confirm payment landed",
    line: "Got it, payment's in. You're locked. That $100 comes straight off your total.",
    required: true,
    source: "14 §1",
  },
  {
    id: "disclosures",
    label: "Disclosure block — every booking",
    line: "Last thing, let me give you the quick rundown so there are no surprises.",
    required: true,
    source: "14 §5 (§8: never collect a deposit without it)",
  },
  {
    id: "dropoff_framing",
    label: "Drop-off framing (no clock times)",
    line: "Get the car to us by 11:00 am and it's done same day, and we call you the second it's finished.",
    required: false,
    source: "14 §1",
  },
];

export interface Disclosure {
  id: string;
  trigger: string;
  label: string;
  line: string;
  source: string;
}

/** The disclosure block (14 §5–6) — renders in the close checklist (req G.2). */
export const DISCLOSURES: Disclosure[] = [
  {
    id: "deposit_applies",
    trigger: "booking",
    label: "$100 applies toward total",
    line: "Your $100 deposit applies straight toward your total.",
    source: "14 §5",
  },
  {
    id: "cancel_24h",
    trigger: "booking (tint)",
    label: "24-hour cancel/reschedule notice",
    line: "Cancel or reschedule with at least 24 hours notice and the deposit stays safe.",
    source: "14 §5",
  },
  {
    id: "cancel_48h_ppf",
    trigger: "booking (PPF)",
    label: "48-hour notice for PPF",
    line: "For PPF work it's 48 hours, since we reserve a bigger block of time.",
    source: "14 §5",
  },
  {
    id: "reschedule_2wk",
    trigger: "booking",
    label: "Reschedule within two weeks",
    line: "If you reschedule, the new date just needs to land within two weeks of the original.",
    source: "14 §5",
  },
  {
    id: "no_show",
    trigger: "booking",
    label: "No-show risks the deposit",
    line: "If something comes up day-of, call us — a no-show without a call puts the deposit at risk.",
    source: "14 §5",
  },
  {
    id: "over_1000",
    trigger: "total > $1,000 (or any PPF install)",
    label: "50% at drop-off — say it at booking",
    line: "Since it's over a thousand, half is due at drop-off before we start, and your deposit counts toward it.",
    source: "14 §6 · 02 §5",
  },
  {
    id: "twelve_hour",
    trigger: "can't pay now",
    label: "12-hour commitment window",
    line: "Hit that link within 12 hours and the slot is fully yours. Until then it could get double booked.",
    source: "14 §3",
  },
];

/** The showroom motion (15) — hooks + locations with landmark directions. */
export const SHOWROOM = {
  hooks: [
    "Put your hand behind each film and feel the heat difference yourself.",
    "You'll leave with a 100 percent accurate quote, good for 30 days.",
  ],
  ask: "Offer two days, then the location: \"Would tomorrow or Thursday work better? And which is closer, Indianapolis or Greenwood?\" Never offer a weekend slot.",
  locations: [
    {
      key: "indianapolis",
      name: "Indianapolis",
      address: "4358 Pendleton Pike",
      landmark: "Just west of the 465 and Pendleton Pike interchange",
      hours: "Mon–Fri 9:00–5:30",
    },
    {
      key: "greenwood",
      name: "Greenwood",
      address: "494 N US 31, Suite B",
      landmark: "Two stoplights south of Greenwood Park Mall on US 31",
      hours: "Mon–Fri 9:00–5:30",
    },
  ],
  source: "15_showroom-consult-script-library.md · 02 §1",
} as const;

/** PPF consult + coating inspection paths (req G.6 — the supported close). */
export const CONSULT_PATHS = {
  ppf: {
    label: "Book the PPF consult — never the install",
    line: "We don't book PPF installs by phone — the consult gets you a 100% accurate quote, no charge.",
    preFrames: [
      "PPF installs take 50% down at drop-off; any deposit counts toward it.",
      "PPF customers return within 14–30 days for the follow-up inspection that guarantees the warranty.",
    ],
    source: "15 §3 · 02 §7/§11",
  },
  coating: {
    label: "Book the paint inspection",
    line: "Coating pricing follows the paint — the inspection gets you the real number, accurate and good for 30 days.",
    preFrames: ["Coating is never sold as rock chip protection — that is PPF."],
    source: "15 §4 · 11 § COATING",
  },
} as const;

/** Decline path (req 7.3): a concrete next step, never "just let us know". */
export const DECLINE_OPTIONS = [
  {
    id: "showroom_visit",
    label: "Showroom visit (2nd best)",
    line: "Come feel the heat demo and leave with a 100% accurate quote, good for 30 days. Tomorrow or Thursday?",
    source: "15 §1",
  },
  {
    id: "deposit_12h",
    label: "Hold with 12-hour link",
    line: "I'll set the appointment and text the payment link — hit it within 12 hours and the slot is yours.",
    source: "14 §3",
  },
  {
    id: "text_quote",
    label: "Text quote + dated follow-up",
    line: "I'm texting you the written quote right now, and I'll check back Friday. Does Friday work?",
    source: "14 §7 · 03 § Stage 11",
  },
] as const;

/** Wrap-up completeness sweep (16 §10) — the fields verified before hang-up. */
export const COMPLETENESS_SWEEP = {
  items: ["First + last name", "Phone number", "Vehicle year/make/model", "Service interest", "Email (offered)"],
  line: "Before I let you go, let me make sure I've got everything right so your quote's accurate.",
  source: "16_crm-capture-script-library.md §10",
} as const;
